import { ScamAnalysisResult } from '../types';
import { SCAM_PRESETS } from '../data/scamScenarios';

const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

export async function analyzeScamMessage(
  text: string, 
  channel: 'sms' | 'email' | 'message' | 'job_offer' | 'invoice' | 'marketplace' | 'social_media' = 'sms'
): Promise<ScamAnalysisResult> {
  await delay(850); // AI inference simulation

  const content = text.trim();
  const lower = content.toLowerCase();

  if (!content || content.length < 5) {
    throw new Error('Please paste a complete message or conversation snippet to analyze.');
  }

  // Heuristic analysis indicators
  const urgencyKeywords = ['urgent', 'immediately', 'within 24 hours', 'today only', 'final notice', 'penalty fee', 'action required', 'suspended', 'forfeited', 'locked', 'due today', 'panic'];
  const paymentKeywords = ['payid', 'bsb', 'bank transfer', 'gift card', 'crypto', 'duty fee', 'handling fee', 'deposit', 'refund fee', '$', 'aud', 'unpaid invoice', 'wire'];
  const linkKeywords = ['http://', 'https://', 'www.', '.com', '.net', '.xyz', '.org', 'click here', 'update details', 'claim refund', 'tinyurl', 'bit.ly', 'login'];
  const sensitiveKeywords = ['password', 'card number', 'cvv', 'driver licence', 'medicare', 'tfn', 'netcode', 'otp', 'verification code', 'pin', 'id card', 'passport'];
  const govOrBrandKeywords = ['auspost', 'australia post', 'linkt', 'transurban', 'mygov', 'ato', 'tax office', 'services australia', 'commbank', 'nab', 'anz', 'westpac', 'centrelink', 'medicare'];

  const hasUrgency = urgencyKeywords.some(k => lower.includes(k));
  const hasSuspiciousPayment = paymentKeywords.some(k => lower.includes(k));
  const hasLink = linkKeywords.some(k => lower.includes(k));
  const hasSensitive = sensitiveKeywords.some(k => lower.includes(k));
  
  let targetBrand: string | undefined = undefined;
  for (const brand of govOrBrandKeywords) {
    if (lower.includes(brand)) {
      targetBrand = brand.toUpperCase();
      break;
    }
  }

  // Calculate dynamic risk score
  let score = 15;
  const reasons: string[] = [];
  const recommendedActions: string[] = [];

  if (hasUrgency) {
    score += 25;
    reasons.push('High psychological pressure and manufactured urgency detected to provoke hasty action.');
  }
  if (hasSuspiciousPayment) {
    score += 30;
    reasons.push('Requests direct bank transfer, PayID modification, or unexpected fee settlement.');
  }
  if (hasLink) {
    score += 20;
    reasons.push('Contains external links or redirections to non-official domains.');
  }
  if (hasSensitive) {
    score += 25;
    reasons.push('Solicits sensitive credentials, payment tokens, or Australian identity details.');
  }
  if (targetBrand) {
    score += 15;
    reasons.push(`Apparent impersonation or reference to known Australian organization (${targetBrand}).`);
  }

  // If text is matched with a known preset, enrich with accurate expert data
  const matchedPreset = SCAM_PRESETS.find(p => lower.includes(p.previewText.slice(0, 20).toLowerCase()) || p.fullContent.slice(0, 30).toLowerCase() === lower.slice(0, 30));
  
  if (matchedPreset) {
    score = matchedPreset.riskScore;
  }

  score = Math.min(Math.max(score, 5), 99);

  let riskLevel: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL' = 'LOW';
  if (score >= 85) riskLevel = 'CRITICAL';
  else if (score >= 60) riskLevel = 'HIGH';
  else if (score >= 35) riskLevel = 'MEDIUM';
  else riskLevel = 'LOW';

  if (riskLevel === 'CRITICAL' || riskLevel === 'HIGH') {
    recommendedActions.push('Do NOT click any links, open attachments, or reply to this message.');
    recommendedActions.push('Do NOT send money, gift cards, or PayID refunds under any circumstances.');
    recommendedActions.push('Contact the organization directly via their official Australian phone number or verified app.');
    recommendedActions.push('Report the message to Scamwatch (scamwatch.gov.au) or forward SMS to 7726 (SPAM).');
    recommendedActions.push('If you already entered card/bank details, notify your financial institution immediately.');
  } else if (riskLevel === 'MEDIUM') {
    recommendedActions.push('Exercise caution. Verify the sender independently before responding.');
    recommendedActions.push('Never disclose one-time SMS passcodes or passwords.');
  } else {
    recommendedActions.push('No obvious high-risk scam indicators detected in this text snippet.');
    recommendedActions.push('Always stay vigilant: legitimate banks will never ask for your passwords or NetCode via SMS/email.');
  }

  return {
    id: 'scam-chk-' + Date.now(),
    inputContent: text,
    channel,
    riskScore: score,
    riskLevel,
    detectedIndicators: {
      urgency: hasUrgency,
      suspiciousPayment: hasSuspiciousPayment,
      suspiciousLink: hasLink,
      senderMismatch: Boolean(targetBrand),
      sensitiveInfoRequest: hasSensitive,
      impersonationTarget: targetBrand
    },
    reasons: reasons.length > 0 ? reasons : ['No common scam heuristics detected in the provided snippet.'],
    recommendedActions,
    officialReporting: {
      scamwatchUrl: 'https://www.scamwatch.gov.au/report-a-scam',
      cyberGovUrl: 'https://www.cyber.gov.au/report-and-recover/report',
      idcarePhone: '1800 595 160'
    },
    checkedAt: new Date().toISOString()
  };
}
